import { useState } from "react";
import { siteData, ExtPartner } from "@/store/siteData";
import { saveImage, getImageUrl } from "@/utils/imageDB";
import { useImageUrl } from "@/hooks/useImageUrl";

function Field({ label, value, onChange, multiline = false }: { label: string; value: string | number; onChange: (v: string) => void; multiline?: boolean }) {
  const cls = "w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6] transition-colors";
  return (
    <div>
      <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">{label}</label>
      {multiline ? <textarea value={String(value)} onChange={e => onChange(e.target.value)} rows={3} className={cls + " resize-none"} /> : <input type="text" value={String(value)} onChange={e => onChange(e.target.value)} className={cls} />}
    </div>
  );
}

function PartnerForm({ partner, onSave, onCancel }: { partner: ExtPartner; onSave: (p: ExtPartner) => void; onCancel: () => void }) {
  const [data, setData] = useState<ExtPartner>({ ...partner });
  const [imgPreview, setImgPreview] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [certInput, setCertInput] = useState("");
  const imgUrl = useImageUrl(data.heroImageId, data.heroImage);

  const upd = (key: keyof ExtPartner, val: string | number) => setData(p => ({ ...p, [key]: val }));

  const handleImageUpload = async (file: File) => {
    const id = await saveImage(file);
    const url = await getImageUrl(id);
    setData(p => ({ ...p, heroImageId: id }));
    if (url) setImgPreview(url);
  };

  const updStat = (i: number, key: "label" | "value", val: string) => {
    const stats = [...data.stats];
    stats[i] = { ...stats[i], [key]: val };
    setData(p => ({ ...p, stats }));
  };

  const updAch = (i: number, key: "year" | "text", val: string) => {
    const ach = [...data.achievements];
    ach[i] = { ...ach[i], [key]: val };
    setData(p => ({ ...p, achievements: ach }));
  };

  const updSvc = (i: number, key: "title" | "desc", val: string) => {
    const svcs = [...data.keyServices];
    svcs[i] = { ...svcs[i], [key]: val };
    setData(p => ({ ...p, keyServices: svcs }));
  };

  const addTag = () => { if (!tagInput.trim()) return; setData(p => ({ ...p, tags: [...p.tags, tagInput.trim()] })); setTagInput(""); };
  const addCert = () => { if (!certInput.trim()) return; setData(p => ({ ...p, certifications: [...p.certifications, certInput.trim()] })); setCertInput(""); };
  const addAch = () => setData(p => ({ ...p, achievements: [...p.achievements, { year: "2026", text: "Yeni kilometre taşı" }] }));
  const addSvc = () => setData(p => ({ ...p, keyServices: [...p.keyServices, { title: "Yeni Hizmet", desc: "Açıklama" }] }));

  const handleSave = () => { onSave(data); setSaved(true); setTimeout(() => setSaved(false), 2000); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold truncate">{data.name}</h3>
        <button onClick={onCancel} className="text-[#7B8FAF] hover:text-white cursor-pointer flex-shrink-0"><i className="ri-close-line text-xl"></i></button>
      </div>

      {/* Hero Image */}
      <div>
        <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Hero Görseli</label>
        <div className="flex items-start gap-3">
          <div className="w-28 h-16 flex-shrink-0 rounded overflow-hidden border border-[#2B3654]">
            <img src={imgPreview || imgUrl} alt="" className="w-full h-full object-cover" />
          </div>
          <label className="flex-1 flex items-center gap-2 cursor-pointer border border-dashed border-[#2B3654] hover:border-[#3B82F6] rounded-lg px-3 py-3 transition-colors">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-cloud-line text-[#3B82F6]"></i></div>
            <span className="text-[#7B8FAF] text-xs">PNG, JPG yükle{data.heroImageId ? " ✓" : ""}</span>
            <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleImageUpload(e.target.files[0])} />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Firma Adı" value={data.name} onChange={v => upd("name", v)} />
        <Field label="Sektör" value={data.sector} onChange={v => upd("sector", v)} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Kuruluş Yılı" value={data.founded} onChange={v => upd("founded", v)} />
        <Field label="Ortaklık Başlangıcı" value={data.since} onChange={v => upd("since", v)} />
        <Field label="Ortak Proje Sayısı" value={data.projects} onChange={v => upd("projects", Number(v))} />
      </div>
      <Field label="Kısa Açıklama" value={data.shortDesc} onChange={v => upd("shortDesc", v)} />
      <Field label="Orta Açıklama" value={data.fullDesc} onChange={v => upd("fullDesc", v)} multiline />
      <Field label="Detaylı Açıklama (paragraflar)" value={data.longDesc} onChange={v => upd("longDesc", v)} multiline />
      <Field label="Web Sitesi URL" value={data.website} onChange={v => upd("website", v)} />

      {/* Stats */}
      <div>
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">İstatistikler (3 kutu)</p>
        <div className="space-y-2">
          {data.stats.map((s, i) => (
            <div key={i} className="grid grid-cols-2 gap-3 bg-[#0F1729] p-3 rounded-lg border border-[#2B3654]">
              <Field label="Değer" value={s.value} onChange={v => updStat(i, "value", v)} />
              <Field label="Etiket" value={s.label} onChange={v => updStat(i, "label", v)} />
            </div>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div>
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Etiketler</p>
        <div className="flex gap-2 mb-2">
          <input type="text" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === "Enter" && addTag()} placeholder="Etiket ekle" className="flex-1 bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
          <button onClick={addTag} className="px-3 py-2 bg-[#3B82F6]/20 text-[#B8C5D8] border border-[#3B82F6]/40 rounded-lg text-sm cursor-pointer whitespace-nowrap">Ekle</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {data.tags.map((t, i) => (
            <span key={i} className="flex items-center gap-1.5 text-xs bg-[#1E2B4A] text-[#B8C5D8] border border-[#2B3654] px-3 py-1 rounded-full">
              {t} <button onClick={() => setData(p => ({ ...p, tags: p.tags.filter((_, idx) => idx !== i) }))} className="text-[#5E7398] hover:text-red-400 cursor-pointer"><i className="ri-close-line text-xs"></i></button>
            </span>
          ))}
        </div>
      </div>

      {/* Certifications */}
      <div>
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Sertifikalar</p>
        <div className="flex gap-2 mb-2">
          <input type="text" value={certInput} onChange={e => setCertInput(e.target.value)} onKeyDown={e => e.key === "Enter" && addCert()} placeholder="Sertifika ekle" className="flex-1 bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
          <button onClick={addCert} className="px-3 py-2 bg-[#3B82F6]/20 text-[#B8C5D8] border border-[#3B82F6]/40 rounded-lg text-sm cursor-pointer whitespace-nowrap">Ekle</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {data.certifications.map((c, i) => (
            <span key={i} className="flex items-center gap-1.5 text-xs bg-[#1E2B4A] text-[#B8C5D8] border border-[#2B3654] px-3 py-1 rounded-full">
              {c} <button onClick={() => setData(p => ({ ...p, certifications: p.certifications.filter((_, idx) => idx !== i) }))} className="text-[#5E7398] hover:text-red-400 cursor-pointer"><i className="ri-close-line text-xs"></i></button>
            </span>
          ))}
        </div>
      </div>

      {/* Key Services */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[#7B8FAF] text-xs uppercase tracking-widest">Hizmetler</p>
          <button onClick={addSvc} className="text-xs text-[#B8C5D8] border border-[#2B3654] px-2 py-1 rounded hover:border-[#3B82F6] cursor-pointer whitespace-nowrap">+ Ekle</button>
        </div>
        <div className="space-y-2">
          {data.keyServices.map((s, i) => (
            <div key={i} className="bg-[#0F1729] p-3 rounded-lg border border-[#2B3654] space-y-2">
              <div className="flex items-center justify-between">
                <Field label="Başlık" value={s.title} onChange={v => updSvc(i, "title", v)} />
                <button onClick={() => setData(p => ({ ...p, keyServices: p.keyServices.filter((_, idx) => idx !== i) }))} className="ml-2 mt-4 text-red-400 cursor-pointer flex-shrink-0"><i className="ri-delete-bin-line text-sm"></i></button>
              </div>
              <Field label="Açıklama" value={s.desc} onChange={v => updSvc(i, "desc", v)} multiline />
            </div>
          ))}
        </div>
      </div>

      {/* Achievements */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[#7B8FAF] text-xs uppercase tracking-widest">Kilometre Taşları</p>
          <button onClick={addAch} className="text-xs text-[#B8C5D8] border border-[#2B3654] px-2 py-1 rounded hover:border-[#3B82F6] cursor-pointer whitespace-nowrap">+ Ekle</button>
        </div>
        <div className="space-y-2">
          {data.achievements.map((a, i) => (
            <div key={i} className="flex gap-3 bg-[#0F1729] p-3 rounded-lg border border-[#2B3654] items-start">
              <input type="text" value={a.year} onChange={e => updAch(i, "year", e.target.value)} className="w-16 bg-[#141E35] border border-[#2B3654] text-[#3B82F6] font-bold text-sm rounded px-2 py-1.5 outline-none focus:border-[#3B82F6]" />
              <input type="text" value={a.text} onChange={e => updAch(i, "text", e.target.value)} className="flex-1 bg-[#141E35] border border-[#2B3654] text-white text-sm rounded px-2 py-1.5 outline-none focus:border-[#3B82F6]" />
              <button onClick={() => setData(p => ({ ...p, achievements: p.achievements.filter((_, idx) => idx !== i) }))} className="text-red-400 cursor-pointer flex-shrink-0"><i className="ri-delete-bin-line text-sm"></i></button>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-4 border-t border-[#2B3654]">
        <button onClick={handleSave} className={`w-full py-2.5 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"}`}>
          {saved ? "✓ Kaydedildi" : "Değişiklikleri Kaydet"}
        </button>
      </div>
    </div>
  );
}

function PartnerRowItem({ partner, active, onClick }: { partner: ExtPartner; active: boolean; onClick: () => void }) {
  const imgUrl = useImageUrl(partner.heroImageId, partner.heroImage);
  return (
    <button onClick={onClick} className={`w-full flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all text-left ${active ? "bg-[#3B82F6]/15 border border-[#3B82F6]/40" : "hover:bg-white/5 border border-transparent"}`}>
      <div className="w-12 h-9 flex-shrink-0 rounded overflow-hidden bg-[#1E2B4A]">
        <img src={imgUrl} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{partner.name}</p>
        <p className="text-[#7B8FAF] text-xs truncate">{partner.sector}</p>
      </div>
    </button>
  );
}

interface PartnersEditorProps {
  initialId?: number | null;
}

export default function PartnersEditor({ initialId }: PartnersEditorProps) {
  const [partners, setPartners] = useState<ExtPartner[]>(siteData.getPartners());
  const [selectedId, setSelectedId] = useState<number | null>(initialId ?? null);
  const selected = partners.find(p => p.id === selectedId) ?? null;

  const handleSave = (updated: ExtPartner) => {
    const next = partners.map(p => p.id === updated.id ? updated : p);
    setPartners(next);
    siteData.savePartners(next);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-white tracking-tight">Stratejik Ortaklar</h1>
        <p className="text-[#7B8FAF] text-sm mt-0.5">{partners.length} ortak · Tüm bilgileri, görselleri ve içerikleri düzenle</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 bg-[#141E35] border border-[#2B3654] rounded-xl p-3 space-y-1 max-h-[75vh] overflow-y-auto">
          {partners.map(p => (
            <PartnerRowItem key={p.id} partner={p} active={selectedId === p.id} onClick={() => setSelectedId(p.id)} />
          ))}
        </div>
        <div className="lg:col-span-3 bg-[#141E35] border border-[#2B3654] rounded-xl p-6 max-h-[75vh] overflow-y-auto">
          {selected ? (
            <PartnerForm partner={selected} onSave={handleSave} onCancel={() => setSelectedId(null)} />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <div className="w-14 h-14 flex items-center justify-center bg-[#0F1729] rounded-xl mb-4">
                <i className="ri-links-line text-3xl text-[#3B82F6]"></i>
              </div>
              <p className="text-white font-medium">Ortak seçin</p>
              <p className="text-[#7B8FAF] text-sm mt-1">Düzenlemek için soldan bir ortak seçin</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
